import React, { useState } from 'react';
import { useFormularioRespostas } from '@/hooks/useFormularioRespostas';
import { FormularioResposta } from '@/types/formulario';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Trash2, CheckCircle, XCircle, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormularioRespostaDetalhes } from './FormularioRespostaDetalhes';
import { FormularioAnalyticsDashboard } from './FormularioAnalyticsDashboard';

interface FormularioRespostasListProps {
  formularioId: string;
  formularioTitulo: string;
  onClose: () => void;
}

export function FormularioRespostasList({
  formularioId,
  formularioTitulo,
  onClose,
}: FormularioRespostasListProps) {
  const { respostas, isLoading, deleteResposta, updateRespostaStatus } = useFormularioRespostas(formularioId);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [respostaSelecionada, setRespostaSelecionada] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('respostas');

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      rascunho: { variant: 'secondary', label: 'Rascunho' },
      concluido: { variant: 'default', label: 'Concluído' },
      validado: { variant: 'default', label: 'Validado' },
      rejeitado: { variant: 'destructive', label: 'Rejeitado' },
    };
    const config = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const respostasFiltradas = respostas?.filter((r) => {
    if (filtroStatus === 'todos') return true;
    return r.status === filtroStatus;
  });

  if (respostaSelecionada) {
    return (
      <FormularioRespostaDetalhes
        respostaId={respostaSelecionada}
        onClose={() => setRespostaSelecionada(null)}
        onBack={() => setRespostaSelecionada(null)}
      />
    );
  }

  if (activeTab === 'dashboard') {
    return (
      <FormularioAnalyticsDashboard
        formularioId={formularioId}
        formularioTitulo={formularioTitulo}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-5xl h-[90vh] bg-background shadow-lg overflow-y-auto rounded-lg m-4">
        <div className="container max-w-4xl py-8">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl">Respostas - {formularioTitulo}</CardTitle>
                  <CardDescription className="mt-2">
                    Total de respostas: {respostas?.length || 0}
                  </CardDescription>
                </div>
                <Button variant="ghost" onClick={onClose}>✕</Button>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList>
                  <TabsTrigger value="respostas">📋 Respostas</TabsTrigger>
                  <TabsTrigger value="dashboard">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Dashboard
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Filtros */}
              <div className="flex gap-4 mt-4">
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filtrar por status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="rascunho">Rascunho</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                    <SelectItem value="validado">Validado</SelectItem>
                    <SelectItem value="rejeitado">Rejeitado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>

            <CardContent>
              {isLoading ? (
                <p className="text-center text-muted-foreground">Carregando respostas...</p>
              ) : respostasFiltradas?.length === 0 ? (
                <p className="text-center text-muted-foreground">Nenhuma resposta encontrada</p>
              ) : (
                <div className="space-y-4">
                  {respostasFiltradas?.map((resposta) => (
                    <Card key={resposta.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-medium">{resposta.respondente?.name || 'Anônimo'}</span>
                              {getStatusBadge(resposta.status)}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <p>Iniciado em: {format(new Date(resposta.data_inicio), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                              {resposta.data_conclusao && (
                                <p>Concluído em: {format(new Date(resposta.data_conclusao), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {resposta.status === 'concluido' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateRespostaStatus({ respostaId: resposta.id, status: 'validado' })}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Validar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateRespostaStatus({ respostaId: resposta.id, status: 'rejeitado' })}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Rejeitar
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRespostaSelecionada(resposta.id)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (confirm('Deseja realmente excluir esta resposta?')) {
                                  deleteResposta(resposta.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
